import {
  useCollaborationHighlightStore,
  type CollaborationHighlightContext,
  type CollaborationHighlightTargetType,
} from '@/stores/collaborationHighlightStore';

type MediaHighlightRoute = {
  projectId: string;
  parentProjectId?: string | null;
  context: CollaborationHighlightContext;
};

export function getVoiceoverHighlightScope(voiceoverId: string): string {
  return `voiceover:${voiceoverId}`;
}

export function getStoryboardVoiceTrackHighlightScope(storyboardId: string): string {
  return `storyboard-voice-track:${storyboardId}`;
}

export function getStoryboardVideoHighlightScope(videoId: string): string {
  return `storyboard-video:${videoId}`;
}

export function getStoryboardVideoTrackHighlightScope(storyboardId: string): string {
  return `storyboard-video-track:${storyboardId}`;
}

export function acknowledgeVoiceoverTargetHighlight(params: {
  route: MediaHighlightRoute;
  voiceoverId: string;
  dismissRouteScope: (scope: string) => void;
}): void {
  const { route, voiceoverId, dismissRouteScope } = params;
  useCollaborationHighlightStore.getState().clearHighlight({
    projectId: route.projectId,
    parentProjectId: route.parentProjectId,
    context: route.context,
    targetType: 'voiceover',
    targetId: voiceoverId,
  });
  dismissRouteScope(getVoiceoverHighlightScope(voiceoverId));
}

export function acknowledgeStoryboardVoiceTrackHighlight(params: {
  route: MediaHighlightRoute;
  storyboardId: string;
  dismissRouteScope: (scope: string) => void;
}): void {
  clearStoryboardScopedHighlights({
    ...params,
    targetTypes: ['voiceover'],
    dismissedScope: getStoryboardVoiceTrackHighlightScope(params.storyboardId),
  });
}

export function acknowledgeStoryboardVideoTargetHighlight(params: {
  route: MediaHighlightRoute;
  videoId: string;
  dismissRouteScope: (scope: string) => void;
}): void {
  const { route, videoId, dismissRouteScope } = params;
  useCollaborationHighlightStore.getState().clearHighlight({
    projectId: route.projectId,
    parentProjectId: route.parentProjectId,
    context: route.context,
    targetType: 'storyboard_video',
    targetId: videoId,
  });
  dismissRouteScope(getStoryboardVideoHighlightScope(videoId));
}

export function acknowledgeStoryboardVideoTrackHighlight(params: {
  route: MediaHighlightRoute;
  storyboardId: string;
  dismissRouteScope: (scope: string) => void;
}): void {
  clearStoryboardScopedHighlights({
    ...params,
    targetTypes: ['storyboard_video'],
    dismissedScope: getStoryboardVideoTrackHighlightScope(params.storyboardId),
  });
}

function clearStoryboardScopedHighlights(params: {
  route: MediaHighlightRoute;
  storyboardId: string;
  targetTypes: readonly CollaborationHighlightTargetType[];
  dismissRouteScope: (scope: string) => void;
  dismissedScope: string;
}): void {
  const store = useCollaborationHighlightStore.getState();
  for (const item of Object.values(store.byEventId)) {
    const event = item.event;
    if (
      !params.targetTypes.includes(event.targetType) ||
      event.targetStoryboardId !== params.storyboardId ||
      event.context !== params.route.context
    ) {
      continue;
    }
    store.clearHighlight({
      projectId: params.route.projectId,
      parentProjectId: params.route.parentProjectId,
      context: params.route.context,
      targetType: event.targetType,
      targetId: event.targetId,
    });
  }
  params.dismissRouteScope(params.dismissedScope);
}
